import { Warehouse } from '@domain/inventory/models/Warehouse';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';

export type WarehouseInfoCategory = 'connected' | 'csv' | 'manual';

const PLATFORM_CONNECTION_TYPES: WarehouseConnectionType[] = [
  WarehouseConnectionType.BOL,
  WarehouseConnectionType.MONTA,
  WarehouseConnectionType.WOOCOMMERCE,
  WarehouseConnectionType.IMPORT4YOU,
  WarehouseConnectionType.PRODUCT_FLOW,
  WarehouseConnectionType.SHOPIFY,
  WarehouseConnectionType.AMAZON,
  WarehouseConnectionType.CHANNELDOCK,
];

const CATEGORY_ORDER: WarehouseInfoCategory[] = ['connected', 'csv', 'manual'];

export const WAREHOUSE_INFO_COPY: Record<
  WarehouseInfoCategory,
  { tabLabel: string; description: string; videoSrc?: string }
> = {
  connected: {
    tabLabel: 'Connected',
    description:
      'For connected warehouses like Monta and Shopify, we load your data automatically. Stock levels sync every hour.',
    videoSrc: '/assets/videos/onboarding/warehouse-connected.mp4',
  },
  csv: {
    tabLabel: 'CSV',
    description:
      'For CSV warehouses, you can easily upload a new file to update your stock levels.',
    videoSrc: '/assets/videos/onboarding/warehouse-csv.mp4',
  },
  manual: {
    tabLabel: 'Manual',
    description: 'For manual warehouses, you can update your stock levels yourself.',
    videoSrc: '/assets/videos/onboarding/warehouse-manual.mp4',
  },
};

export function getPrimaryConnectionType(
  warehouse: Warehouse,
): WarehouseConnectionType | null {
  const connections = warehouse.warehouseConnections ?? [];
  if (connections.length === 0) return null;

  const platform = connections.find((c) =>
    PLATFORM_CONNECTION_TYPES.includes(c.sourceType),
  );
  if (platform) return platform.sourceType;

  const file = connections.find((c) => c.sourceType === WarehouseConnectionType.FILE);
  if (file) return file.sourceType;

  return connections[0]?.sourceType ?? null;
}

export function getWarehouseInfoCategory(warehouse: Warehouse): WarehouseInfoCategory {
  const sourceType = getPrimaryConnectionType(warehouse);
  if (sourceType === WarehouseConnectionType.FILE) return 'csv';
  if (sourceType && PLATFORM_CONNECTION_TYPES.includes(sourceType)) return 'connected';
  return 'manual';
}

/** Categories present among the user's warehouses, in Connected → CSV → Manual order. */
export function getPresentInfoCategories(
  warehouses: Warehouse[],
): WarehouseInfoCategory[] {
  const present = new Set(warehouses.map(getWarehouseInfoCategory));
  return CATEGORY_ORDER.filter((category) => present.has(category));
}

export function getConnectionTypeLabel(
  sourceType: WarehouseConnectionType | null,
): string {
  switch (sourceType) {
    case WarehouseConnectionType.BOL:
      return 'Bol';
    case WarehouseConnectionType.MONTA:
      return 'Monta';
    case WarehouseConnectionType.SHOPIFY:
      return 'Shopify';
    case WarehouseConnectionType.WOOCOMMERCE:
      return 'WooCommerce';
    case WarehouseConnectionType.AMAZON:
      return 'Amazon';
    case WarehouseConnectionType.CHANNELDOCK:
      return 'ChannelDock';
    case WarehouseConnectionType.IMPORT4YOU:
      return 'Import4You';
    case WarehouseConnectionType.PRODUCT_FLOW:
      return 'Product Flow';
    case WarehouseConnectionType.FILE:
      return 'CSV';
    case WarehouseConnectionType.MANUAL:
    case null:
      return 'Manual';
    default:
      return 'Warehouse';
  }
}
